/**
 * PÁGINA DE LOGIN ADMINISTRATIVO
 * ===============================
 * Esta página permite el ingreso exclusivo para administradores del sistema.
 * Solo los usuarios con credenciales de administrador pueden acceder.
 * 
 * COMPONENTES:
 * - Formulario de login con email y contraseña
 * - Validación de credenciales
 * - Redirección al panel de administración tras login exitoso
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

const AdminLoginPage = () => {
  // Estado para almacenar el email ingresado por el usuario
  const [email, setEmail] = useState('');
  
  // Estado para almacenar la contraseña ingresada
  const [password, setPassword] = useState('');
  
  // Estado para mostrar/ocultar la contraseña
  const [showPassword, setShowPassword] = useState(false);
  
  // Estado para manejar mensajes de error
  const [error, setError] = useState('');
  
  // Hook de React Router para navegar entre páginas
  const navigate = useNavigate();

  /**
   * Función que se ejecuta cuando el usuario envía el formulario
   * @param {Event} e - Evento del formulario
   */
  const handleLogin = (e) => {
    // Prevenir que la página se recargue al enviar el formulario
    e.preventDefault();
    
    // Limpiar mensajes de error previos
    setError('');

    // VALIDACIÓN BÁSICA
    // Verificar que ambos campos estén llenos
    if (!email || !password) {
      setError('Por favor complete todos los campos');
      return;
    }

    // TODO: Aquí irá la lógica de autenticación real
    // Por ahora, simulamos un login exitoso
    console.log('Intentando login con:', email, password);
    
    // Simular autenticación exitosa y redirigir al dashboard
    // navigate('/admin/dashboard');
    
    // Mensaje temporal mientras se implementa la autenticación
    setError('Funcionalidad en desarrollo. Contacte al administrador del sistema.');
  };

  return (
    // Contenedor principal con altura completa de la pantalla
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-green-900 to-gray-900 flex items-center justify-center px-4">
      
      {/* Efectos de fondo decorativos */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-500 rounded-full blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Tarjeta de login */}
      <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl p-8 md:p-12 w-full max-w-md border border-white/20 shadow-2xl">
        
        {/* Icono de candado en la parte superior */}
        <div className="flex justify-center mb-6">
          <div className="bg-green-600 rounded-full p-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
        </div>

        {/* Título de la página */}
        <h1 className="text-3xl font-bold text-white text-center mb-2">
          Ingreso Privado
        </h1>
        <p className="text-gray-300 text-center mb-8">
          Acceso exclusivo para administradores
        </p>

        {/* Formulario de login */}
        <form onSubmit={handleLogin} className="space-y-6">
          
          {/* Campo de Email */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Email Administrativo
            </label>
            <div className="relative">
              {/* Icono de mail dentro del input */}
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg pl-12 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-400 transition-all"
                placeholder="admin@plazarural.com"
              />
            </div>
          </div>

          {/* Campo de Contraseña */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">
              Contraseña
            </label>
            <div className="relative">
              {/* Icono de candado dentro del input */}
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/10 border border-white/20 rounded-lg pl-12 pr-12 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-green-400 transition-all"
                placeholder="••••••••"
              />
              {/* Botón para mostrar/ocultar contraseña */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mensaje de error (si existe) */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-200 text-sm">
              {error}
            </div>
          )}

          {/* Botón de envío */}
          <button
            type="submit"
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Ingresar al Panel
          </button>
        </form>

        {/* Link para volver al inicio */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-300 hover:text-white text-sm transition-colors"
          >
            ← Volver al sitio público
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;